import type { VatTreatment } from "./vat";
import { api } from "../lib/api";
import { session } from "../stores/session";
import type { RateUnit } from "./project";

export type InvoiceStatus =
  | "draft"
  | "issued"
  | "sent"
  | "part_paid"
  | "paid"
  | "overdue"
  | "void"
  | "written_off";

export type PaymentMethod = "bank_transfer" | "card" | "cash" | "other";
export type InvoiceKind = "invoice" | "credit_note";

export interface InvoiceLine {
  id: string;
  project_id: string | null;
  description: string;
  quantity: string;
  unit_price: string;
  unit: RateUnit;
  position: number;
  line_total: string;
}

export interface InvoiceLineInput {
  project_id: string | null;
  description: string;
  quantity: string;
  unit_price: string;
  unit: RateUnit;
}

export interface Payment {
  id: string;
  invoice_id: string;
  date: string;
  amount: string;
  method: PaymentMethod;
  reference: string | null;
  bank_transaction_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface PaymentInput {
  date: string;
  amount: string;
  method: PaymentMethod;
  reference: string | null;
}

/** List rows: scalar display fields only — no child collections (03-api-spec.md). */
export interface InvoiceSummary {
  id: string;
  company_id: string;
  client_id: string;
  client_name: string;
  number: string | null;
  is_backdated: boolean;
  kind: InvoiceKind;
  status: InvoiceStatus;
  issue_date: string;
  due_date: string;
  currency: string;
  fx_rate_to_gbp: string;
  vat_rate: string;
  vat_treatment: VatTreatment;
  subtotal: string;
  vat_amount: string;
  total: string;
  amount_paid: string;
  po_reference: string | null;
  notes_public: string | null;
  notes_internal: string | null;
  pdf_path: string | null;
  pdf_generated_at: string | null;
  pdf_uploaded_at: string | null;
  pdf_uploaded_filename: string | null;
  timesheet_pdf_path: string | null;
  timesheet_pdf_generated_at: string | null;
  voided_at: string | null;
  void_reason: string | null;
  created_at: string;
  updated_at: string;
  overpaid: boolean;
  days_overdue: number;
  closed_year_warning: string | null;
}

export interface Invoice extends InvoiceSummary {
  lines: InvoiceLine[];
  payments: Payment[];
  // Attached-timesheet panel fields (issued view) — relationship-derived, detail only.
  timesheet_entry_count: number;
  timesheet_from: string | null;
  timesheet_to: string | null;
}

export interface InvoiceCreateInput {
  client_id: string;
  issue_date?: string | null;
  due_date?: string | null;
  currency?: string | null;
  vat_rate?: string | null;
  vat_treatment?: VatTreatment | null;
  po_reference?: string | null;
  notes_public?: string | null;
  notes_internal?: string | null;
  lines: InvoiceLineInput[];
  /** Credit notes (Phase 5) share the invoice table/numbering sequence and every other
   * invoice endpoint (issue/void/duplicate/PDF) via this field, set once at creation. */
  kind?: InvoiceKind;
}

export interface InvoiceUpdateInput {
  client_id: string;
  issue_date: string;
  due_date: string;
  currency: string;
  vat_rate: string;
  // Nullable, matching the server: null means "infer from the rate".
  vat_treatment: VatTreatment | null;
  po_reference: string | null;
  notes_public: string | null;
  notes_internal: string | null;
  lines: InvoiceLineInput[];
  updated_at: string;
}

export interface InvoiceListFilters {
  status?: InvoiceStatus;
  client_id?: string;
  date_from?: string;
  date_to?: string;
  q?: string;
  unpaid?: boolean;
  page?: number;
  page_size?: number;
}

export function listInvoices(
  filters: InvoiceListFilters = {},
): Promise<{ items: InvoiceSummary[]; total: number }> {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== "") params.set(key, String(value));
  }
  const query = params.toString();
  return api.getPaged<InvoiceSummary>(`/api/invoices${query ? `?${query}` : ""}`);
}

export function getInvoice(id: string): Promise<Invoice> {
  return api.get<Invoice>(`/api/invoices/${id}`);
}

export function createInvoice(input: InvoiceCreateInput): Promise<Invoice> {
  return api.post<Invoice>("/api/invoices", input);
}

export function updateInvoice(id: string, input: InvoiceUpdateInput): Promise<Invoice> {
  return api.put<Invoice>(`/api/invoices/${id}`, input);
}

export function deleteInvoice(id: string): Promise<void> {
  return api.delete(`/api/invoices/${id}`);
}

export interface IssueOptions {
  fx_rate_to_gbp?: string | null;
  /** Sent only when the user has explicitly confirmed the drafted dates (issue date set
   * to today in the editor) — otherwise the server stamps today + payment terms. */
  issue_date?: string | null;
  due_date?: string | null;
  /** Historical/backdated entry (e.g. re-entering last year's accounts): a custom
   * number outside the normal auto-numbered sequence. Requires issue_date too — the
   * server 422s a number sent without one. */
  number?: string | null;
}

export function issueInvoice(id: string, options: IssueOptions = {}): Promise<Invoice> {
  return api.post<Invoice>(`/api/invoices/${id}/issue`, {
    fx_rate_to_gbp: options.fx_rate_to_gbp ?? null,
    issue_date: options.issue_date ?? null,
    due_date: options.due_date ?? null,
    number: options.number ?? null,
  });
}

export function regenerateInvoicePdf(id: string): Promise<Invoice> {
  return api.post<Invoice>(`/api/invoices/${id}/pdf/regenerate`);
}

/** Historical invoices only — the real document that went out on the old letterhead. */
export function uploadInvoicePdf(id: string, file: File): Promise<Invoice> {
  return api.upload<Invoice>(`/api/invoices/${id}/pdf/upload`, file);
}

export function removeInvoicePdfUpload(id: string): Promise<Invoice> {
  return api.delete<Invoice>(`/api/invoices/${id}/pdf/upload`);
}

export function voidInvoice(id: string, reason: string): Promise<Invoice> {
  return api.post<Invoice>(`/api/invoices/${id}/void`, { reason });
}

export function duplicateInvoice(id: string): Promise<Invoice> {
  return api.post<Invoice>(`/api/invoices/${id}/duplicate`);
}

export interface FromTimesheetInput {
  client_id: string;
  from: string;
  to: string;
  project_ids?: string[];
}

/** POST /invoices/from-timesheet: rolls up the client's uninvoiced entries in range
 * into a new draft (05 § Timesheets). A 422 currency_mismatch names the offending
 * project(s) in error.fields; a 400 no_entries means nothing uninvoiced in range. */
export function createInvoiceFromTimesheet(input: FromTimesheetInput): Promise<Invoice> {
  return api.post<Invoice>("/api/invoices/from-timesheet", input);
}

export interface AttachTimesheetEntriesInput {
  entry_ids: string[];
  /** true regenerates the invoice's lines from this set (wholesale replace); false only
   * changes attachment, existing lines are left untouched. */
  rollup: boolean;
}

export function updateInvoiceTimesheetEntries(
  id: string,
  input: AttachTimesheetEntriesInput,
): Promise<Invoice> {
  return api.put<Invoice>(`/api/invoices/${id}/timesheet-entries`, input);
}

export function regenerateInvoiceTimesheetPdf(id: string): Promise<Invoice> {
  return api.post<Invoice>(`/api/invoices/${id}/timesheet.pdf/regenerate`);
}

/** Same "browser navigates directly" X-Accel-Redirect pattern as invoicePdfUrl. */
export function invoiceTimesheetPdfUrl(id: string): string {
  const params = new URLSearchParams({ company_id: session.companyId.value ?? "" });
  return `/api/invoices/${id}/timesheet.pdf?${params.toString()}`;
}

/** Not fetched via the JSON api client — the server responds with X-Accel-Redirect
 * (nginx streams the actual bytes), so the browser must navigate/link to this URL
 * directly, same pattern as the company logo (views/companies.ts). A plain browser GET
 * can't attach the X-Company-Id header the JSON API uses, so the active company rides
 * along as ?company_id instead (the backend accepts either — 03-api-spec.md). */
export function invoicePdfUrl(id: string): string {
  const params = new URLSearchParams({ company_id: session.companyId.value ?? "" });
  return `/api/invoices/${id}/pdf?${params.toString()}`;
}

/** Live render of current state — draft or issued, never persisted server-side.
 * Same "browser navigates directly" pattern as invoicePdfUrl. */
export function invoicePreviewPdfUrl(id: string): string {
  const params = new URLSearchParams({ company_id: session.companyId.value ?? "" });
  return `/api/invoices/${id}/preview.pdf?${params.toString()}`;
}

/** Live render of the attached timesheet entries, draft or issued, never stored — the
 * timesheet counterpart of invoicePreviewPdfUrl. */
export function invoiceTimesheetPreviewPdfUrl(id: string): string {
  const params = new URLSearchParams({ company_id: session.companyId.value ?? "" });
  return `/api/invoices/${id}/timesheet-preview.pdf?${params.toString()}`;
}

export function listPayments(invoiceId: string): Promise<Payment[]> {
  return api.get<Payment[]>(`/api/invoices/${invoiceId}/payments`);
}

export function createPayment(invoiceId: string, input: PaymentInput): Promise<Invoice> {
  return api.post<Invoice>(`/api/invoices/${invoiceId}/payments`, input);
}

export function updatePayment(
  paymentId: string,
  input: PaymentInput & { updated_at: string },
): Promise<Invoice> {
  return api.put<Invoice>(`/api/payments/${paymentId}`, input);
}

export function deletePayment(paymentId: string): Promise<Invoice> {
  return api.delete<Invoice>(`/api/payments/${paymentId}`);
}

export type EmailStatus = "queued" | "sent" | "failed";

export type EmailKind = "invoice" | "reminder";

export interface EmailLog {
  id: string;
  invoice_id: string;
  kind: EmailKind;
  to_addresses: string[];
  cc_addresses: string[];
  subject: string;
  body_snapshot: string;
  status: EmailStatus;
  error: string | null;
  sent_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface SendInvoiceInput {
  to?: string[];
  cc?: string[];
  subject?: string;
  body?: string;
}

/** POST /invoices/{id}/send (05 § Email via Fastmail): synchronous — the response
 * already reflects the final sent/failed outcome, not just "queued" (invoice_email.py
 * attempts the real send inline). `idempotencyKey`, when supplied, guards against a
 * double-click producing two emails — a repeat with the same key returns the original
 * email_log untouched rather than sending again (03-api-spec.md cross-cutting). */
export function sendInvoice(
  invoiceId: string,
  input: SendInvoiceInput,
  idempotencyKey?: string,
): Promise<EmailLog> {
  return api.post<EmailLog>(`/api/invoices/${invoiceId}/send`, input, {
    headers: idempotencyKey ? { "Idempotency-Key": idempotencyKey } : undefined,
  });
}

export interface ReminderEligibility {
  remindable: boolean;
  /** Why not, when it can't be sent: not_issued | locked | settled | not_yet_due. */
  reason: string | null;
  message: string;
  days_overdue: number;
  outstanding: string;
  /** The server's own rendered defaults — the modal shows exactly what would go out,
   * rather than a client-side approximation of it. */
  subject: string;
  body: string;
}

/** GET /invoices/{id}/reminder — can this be chased, and what would be sent. Lets the
 * button show or hide, and explain itself, without attempting a send to find out. */
export function getReminderEligibility(invoiceId: string): Promise<ReminderEligibility> {
  return api.get<ReminderEligibility>(`/api/invoices/${invoiceId}/reminder`);
}

/** POST /invoices/{id}/reminder — same transport, log and idempotency contract as
 * sendInvoice; only the content and the guards differ. */
export function sendInvoiceReminder(
  invoiceId: string,
  input: SendInvoiceInput,
  idempotencyKey?: string,
): Promise<EmailLog> {
  return api.post<EmailLog>(`/api/invoices/${invoiceId}/reminder`, input, {
    headers: idempotencyKey ? { "Idempotency-Key": idempotencyKey } : undefined,
  });
}

export function listInvoiceEmails(invoiceId: string): Promise<EmailLog[]> {
  return api.get<EmailLog[]>(`/api/invoices/${invoiceId}/emails`);
}

export function retryInvoiceSend(invoiceId: string, emailLogId: string): Promise<EmailLog> {
  return api.post<EmailLog>(`/api/invoices/${invoiceId}/emails/${emailLogId}/retry`);
}

export function setInvoiceVatTreatment(
  invoiceId: string,
  vatTreatment: VatTreatment,
): Promise<Invoice> {
  return api.post<Invoice>(`/api/invoices/${invoiceId}/vat-treatment`, {
    vat_treatment: vatTreatment,
  });
}

export interface BulkReclassifyResult {
  reclassified: number;
  already_set: number;
  /** Named, not just counted — these still need attention. */
  skipped_locked: string[];
  skipped_rate_mismatch: string[];
}

export function reclassifyClientInvoices(
  clientId: string,
  vatTreatment: VatTreatment,
  setClientDefault: boolean,
): Promise<BulkReclassifyResult> {
  return api.post<BulkReclassifyResult>(`/api/clients/${clientId}/vat-treatment`, {
    vat_treatment: vatTreatment,
    set_client_default: setClientDefault,
  });
}
